import { memo, type MouseEvent, type ReactNode, type Ref } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import RatingStars from '_components/Product/ProductRatingStars';
import type { Swatch } from '@hooks/useHitSwatches';
import PlpColourPicker from '_components/ColourPicker/PlpColourPicker';
import Tag from '_components/UI/Tag';
import { DisplayTag } from '_components/Product/ProductMetaInfo/ProductMetaTags/ProductMetaTags';
import BuyCanadian from '_components/BuyCanadian';
import ProductComparisonCheckbox from '_components/Product/ProductComparisonCheckbox';
import MemberPricingDealsBanner from '@components/product/MemberPricingDealsBanner';

import styles from './ProductCard.module.scss';

/** Also fed to `PlpColourPicker` so hover-preload targets this exact image. */
const PRODUCT_CARD_IMAGE_SIZES =
  '(min-width: 1260px) calc(25vw - 56px), (min-width: 1128px) calc(25vw - 48px), (min-width: 1000px) calc(33.33vw - 51px), (min-width: 767px) calc(50vw - 56px), (min-width: 600px) calc(33.33vw - 43px), (min-width: 374px) calc(50vw - 48px), calc(100vw - 64px)';

export interface ProductCardProps {
  productImage: string;
  brand?: string;
  displayName: string;
  rating?: string;
  reviews?: string;
  guestDisplayPrice?: string;
  productUrl: string;
  displayPrice: string;
  salePrice?: string;
  swatches?: (Swatch & { hexColours?: string[] })[];
  trackClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  priority?: boolean;
  tags?: DisplayTag[];
  isCanadianBrand?: boolean;
  isMadeInCanada?: boolean;
  productCode?: string;
  showMemberPricingDealsBanner?: boolean;
  hasGroupPriceDifference?: boolean;
  isOnSaleOrClearance?: boolean;
  /**
   * Render the member-vs-guest price comparison: the member price as the
   * current price with the guest price struck through beside it.
   */
  showMemberPriceComparison?: boolean;
  dealsBannerRef?: Ref<HTMLDivElement>;
  mini?: boolean;
  /**
   * Inverts the `mini` card surface to white, for strips that sit on a tinted
   * section background (e.g. the PLP cross-linking band on `--neutrals-100`).
   * No effect on the full PLP card.
   */
  onTint?: boolean;
  /** Rendered inside the image wrapper (PLP/search grid only — never on `mini`). */
  quickAdd?: ReactNode;
}

const ProductCard = ({
  productImage,
  brand,
  displayName,
  rating,
  reviews,
  productUrl,
  guestDisplayPrice,
  displayPrice,
  salePrice,
  swatches,
  trackClick,
  priority = false,
  tags = [],
  isCanadianBrand = false,
  isMadeInCanada = false,
  productCode = '',
  showMemberPricingDealsBanner = false,
  hasGroupPriceDifference = false,
  isOnSaleOrClearance = false,
  showMemberPriceComparison = false,
  dealsBannerRef,
  mini = false,
  onTint = false,
  quickAdd,
}: ProductCardProps) => {
  const { t } = useTranslation('common');
  const base = displayName?.replace(/^"|"$/g, '') ?? '';
  const cleanName = (() => {
    if (!brand?.trim()) return base;
    const escaped = brand.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return base.replace(new RegExp(`^${escaped}\\s*`, 'i'), '') || base;
  })();
  const strippedPrice = parseInt(displayPrice?.replace?.(/\D/g, '') || '0');
  const displaySale = !!salePrice;

  const parsedDisplayPrice = parseFloat(
    displayPrice?.replace(/[^0-9.]/g, '') || '0'
  );
  const parsedSalePrice = parseFloat(salePrice?.replace(/[^0-9.]/g, '') || '0');
  const discountPercent =
    displaySale && parsedDisplayPrice > 0
      ? Math.round((1 - parsedSalePrice / parsedDisplayPrice) * 100)
      : 0;

  const showOriginalPrice =
    hasGroupPriceDifference ||
    (isOnSaleOrClearance && parsedSalePrice !== parsedDisplayPrice);
  const originalPrice = hasGroupPriceDifference
    ? guestDisplayPrice
    : displayPrice;

  // Mini variant (ED-171): compact card used inside the mini ProductStrip on the
  // PDP. Link wraps the whole card; no tags, swatches, comparison, or deals banner.
  if (mini) {
    return (
      <article
        className={cn(styles.card, styles.mini, { [styles.onTint]: onTint })}
      >
        <a
          className={cn(styles.link, styles.mini)}
          href={productUrl}
          onClick={(e) => trackClick?.(e)}
        >
          <div className={cn(styles.imageWrapper, styles.mini)}>
            <div className={cn(styles.imagePad, styles.mini)}>
              <Image
                src={productImage}
                alt=""
                fill
                sizes="248px"
                className={styles.image}
                priority={priority}
                loading={priority ? undefined : 'eager'}
              />
            </div>
          </div>
          <div className={cn(styles.info, styles.mini)}>
            <div className={styles.productInfo}>
              {brand && <p className={styles.brandName}>{brand}</p>}
              <p className={cn(styles.productName, styles.mini)}>{cleanName}</p>
            </div>
            {strippedPrice > 0 && (
              <div className={styles.price}>
                <div className={styles.priceGroup}>
                  {displaySale ? (
                    <>
                      <span className={styles.currentPrice}>{salePrice}</span>
                      <span className={styles.originalPrice}>
                        {displayPrice}
                      </span>
                    </>
                  ) : (
                    <span>{displayPrice}</span>
                  )}
                </div>
                {discountPercent > 0 && (
                  <span className={styles.discountBadge}>
                    {t('productStrip.discountBadge', {
                      percent: discountPercent,
                    })}
                  </span>
                )}
              </div>
            )}
            {(rating || reviews) && (
              <div className={styles.ratings}>
                {rating && <RatingStars rating={parseFloat(rating)} />}
                {reviews && (
                  <span className={styles.reviewCount}>({reviews})</span>
                )}
              </div>
            )}
          </div>
        </a>
      </article>
    );
  }

  const hasTags = tags?.length > 0;

  return (
    <article className={styles.card}>
      <div className={styles.imageWrapper}>
        <div className={styles.imagePad}>
          <Image
            src={productImage}
            alt=""
            fill
            // Rendered image width = (100vw - 2×page gutter) × grid column
            // fraction - 2×(.searchHitsItem.newHit padding) - 2×.imagePad's
            // own 12px inset. Grid fractions/padding: Hits.module.scss
            // `.searchHitsItem`/`.newHit`. Page gutter: 16px, or 32px at
            // ≥1260px under FEATURE_FLAG_PLP_FULL_WIDTH (Container.module.scss
            // `.fullWidthGutter`) — assumes that flag's layout, the PLP/search
            // target state, since a static `sizes` can't branch on it.
            sizes={PRODUCT_CARD_IMAGE_SIZES}
            className={styles.image}
            priority={priority}
            loading={priority ? undefined : 'eager'}
          />
        </div>
        <div className={styles.buyCanadianWrapper}>
          {isCanadianBrand && (
            <BuyCanadian
              hit={true}
              isCanadianBrand={!isMadeInCanada}
              isMadeInCanada={isMadeInCanada}
            />
          )}
        </div>
        {quickAdd}
      </div>
      {hasTags && (
        <div className={styles.tagsContainer}>
          {tags.slice(0, 2).map((tag) => (
            <Tag
              key={tag?.name}
              size="small"
              color={tag?.color || 'gray'}
              onDarkBg
            >
              {tag?.name}
            </Tag>
          ))}
        </div>
      )}

      <div className={styles.info}>
        <div className={styles.swatches}>
          <PlpColourPicker
            swatches={swatches ?? []}
            preloadImageSizes={PRODUCT_CARD_IMAGE_SIZES}
          />
        </div>
        <div className={styles.productInfo}>
          {brand && <p className={styles.brandName}>{brand}</p>}
          <p className={styles.productName}>{cleanName}</p>
        </div>
        {strippedPrice > 0 && (
          <div className={styles.price}>
            <div className={styles.priceGroup}>
              {showMemberPriceComparison ? (
                <>
                  <span className={cn(styles.currentPrice, styles.groupPrice)}>
                    {displayPrice}
                  </span>
                  {guestDisplayPrice && (
                    <span className={styles.originalPrice}>
                      {guestDisplayPrice}
                    </span>
                  )}
                </>
              ) : displaySale ? (
                <>
                  <span
                    className={cn(styles.currentPrice, {
                      [styles.groupPrice]: hasGroupPriceDifference,
                    })}
                  >
                    {salePrice}
                  </span>
                  {showOriginalPrice && (
                    <span className={styles.originalPrice}>
                      {originalPrice}
                    </span>
                  )}
                </>
              ) : (
                <span>{displayPrice}</span>
              )}
            </div>
            {isOnSaleOrClearance && discountPercent > 0 && (
              <span className={styles.discountBadge}>
                {t('productStrip.discountBadge', {
                  percent: discountPercent,
                })}
              </span>
            )}
          </div>
        )}
        <div className={styles.ratings}>
          <RatingStars
            rating={rating ? parseFloat(rating) : 0}
            starSize="ExtraSmall"
          />
          {Number(rating) > 0 ? (
            <span className={styles.ratingValue}>{rating}</span>
          ) : (
            <span className={styles.reviewCount}>({reviews || 0})</span>
          )}
        </div>
      </div>
      {showMemberPricingDealsBanner && (
        <div className={styles.dealsBannerContainer} ref={dealsBannerRef}>
          <MemberPricingDealsBanner isPlp={true} />
        </div>
      )}

      <div className={styles.comparisonCheckbox}>
        <ProductComparisonCheckbox product={{ code: productCode }} />
      </div>

      <a
        className={styles.link}
        href={productUrl}
        onClick={(e) => trackClick?.(e)}
        aria-label={[brand, cleanName].filter(Boolean).join(' ')}
      />
    </article>
  );
};

export default memo(ProductCard);
